/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useSelector} from 'react-redux';
import Dashboards from '@ion/applications/src/pages/analytics/Dashboards';
import withPageErrorBoundary from '@ion/applications/src/pages/withPageErrorBoundary';
import {GenericObject} from '@ion/core/src/types/entities';
import Features from '../../security/Features';
import {userSelector} from '../../store/selectors/login-selectors';
import {Route} from '../RouteDefinitions';

/**
 * Renders the main analytics dashboard view, ensuring that translations and
 * page initialization are complete before loading the actual dashboard.
 * While initialization is in progress, a centered content loader is displayed.
 *
 * ```typescript
 * import DashboardsPage from './DashboardsPage';
 *
 * export default function AnalyticsView() {
 *   return <DashboardsPage />;
 * }
 * ```
 * @returns The rendered dashboards page or a loading state 
 *          while waiting for initialization.
 */
const DashboardsPage: React.FC = ({active = true}: {active?: boolean}) => {
  const user = useSelector(userSelector);
  return (
    <Dashboards route={Route.ANALYTICS_DASHBOARDS}
      features={Features.getPermittedFeatures() as GenericObject[]}
      showScheduledReports={Features.hasReportSchedulerBeta()}
      isSchedulerDirector={Features.hasReportSchedulerDirector()}
      isSchedulerFleetManager={Features.hasReportSchedulerFleetManager()}
      user={user}
      active={active} />
  );
};

export default withPageErrorBoundary(DashboardsPage);
